import type { FocusEvent, ReactNode } from "react";
import { XIcon } from "@phosphor-icons/react/X";
import { Row } from "../ComponentLayout/Row";
import { T } from "../Typography/T";
import { DimensionInput } from "./DimensionInput";
import type { Units } from "../Data/Units";
import { describeUnit, Dimension } from "../Data/Units";

/**
 * The freight dimensions input: the length × width × height trio, and
 * optionally a weight field plus a running-total summary line beneath.
 *
 * Omit the weight/summary props for a dimensions-only input (e.g. the
 * saved-commodity editor). The summary tucks up into the fields' reserved error
 * space so it sits snug beneath the inputs — unless a dimension error is
 * showing, in which case it drops below that error.
 *
 * Advisory size warnings match the legacy control: `maxSizeWarning` applies to
 * length, `minSizeWarning` to height. A `weightWarning` the caller worked out is
 * shown on the weight field.
 */

interface DimensionsInputProps {
  length: number | undefined;
  width: number | undefined;
  height: number | undefined;

  onLengthChange: (value: number | undefined) => void;
  onWidthChange: (value: number | undefined) => void;
  onHeightChange: (value: number | undefined) => void;

  lengthErrorMessage?: ReactNode | undefined;
  widthErrorMessage?: ReactNode | undefined;
  heightErrorMessage?: ReactNode | undefined;

  maxSizeWarning?: number | undefined;
  minSizeWarning?: number | undefined;
  /**
   * Shown on the weight field. For a warning no single field can reach on its
   * own — the implied density needs all three axes and the weight together.
   */
  weightWarning?: ReactNode | undefined;

  forceValidation: boolean;

  units: Units;

  onBlur?: ((e: FocusEvent<HTMLInputElement>) => void) | undefined;

  /** Prefix for each field's `data-testid` — e.g. "contents" → `contents_length`. */
  testIdPrefix: string;

  // Optional weight field + running total, rendered inline after the trio.
  weight?: number | undefined;
  onWeightChange?: ((value: number | undefined) => void) | undefined;
  weightErrorMessage?: ReactNode | undefined;
  weightLabel?: string | undefined;
  /** A short running-total line shown beneath the inputs. */
  summary?: string | undefined;
}

// A small bold × between the fields, dropped past the field label to sit on the
// vertical middle of the input value, with breathing room either side.
function Times_() {
  return (
    <XIcon
      weight="bold"
      aria-hidden="true"
      className="mt-7 mr-3 ml-2.5 h-3 w-3 shrink-0 text-content-muted"
    />
  );
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function DimensionsInput(props: DimensionsInputProps) {
  const unitLabel = describeUnit(props.units, Dimension.Length);
  const { weightLabel, onWeightChange } = props;

  const anyError =
    Boolean(props.lengthErrorMessage) ||
    Boolean(props.widthErrorMessage) ||
    Boolean(props.heightErrorMessage) ||
    Boolean(props.weightErrorMessage);

  // A warning occupies the same message line an error does, so the summary has
  // to yield to it for the same reason. It only checked errors, so "Too long?"
  // and the running total were drawn on top of each other. The thresholds route
  // exactly as they do below — max to length, min to height — so this predicts
  // what the two DimensionInputs will show without asking them. The weight
  // warning is the caller's, so it counts too.
  const anyWarning =
    (props.maxSizeWarning !== undefined &&
      props.length !== undefined &&
      props.length > props.maxSizeWarning) ||
    (props.minSizeWarning !== undefined &&
      props.height !== undefined &&
      props.height <= props.minSizeWarning) ||
    props.weightWarning !== undefined;

  const messageInTheWay = anyError || anyWarning;

  return (
    <div>
      {/* Weight drops to its own line when the row is too tight for all four.
          Length × Width × Height are their own Row, so they stay together —
          they read as one expression and shouldn't be split across lines. */}
      <Row gap={5} align="start" wrap>
        <Row gap={0.5} align="start">
          <DimensionInput
            label={`Length (${unitLabel})`}
            value={props.length}
            onChange={props.onLengthChange}
            units={props.units}
            dimension={Dimension.Length}
            errorMessage={props.lengthErrorMessage}
            forceValidation={props.forceValidation}
            onBlur={props.onBlur}
            maxSizeWarning={props.maxSizeWarning}
            widthPx={72}
            dataTestId={`${props.testIdPrefix}_length`}
          />
          <Times_ />
          <DimensionInput
            label={`Width (${unitLabel})`}
            value={props.width}
            onChange={props.onWidthChange}
            units={props.units}
            dimension={Dimension.Length}
            errorMessage={props.widthErrorMessage}
            forceValidation={props.forceValidation}
            onBlur={props.onBlur}
            widthPx={72}
            dataTestId={`${props.testIdPrefix}_width`}
          />
          <Times_ />
          <DimensionInput
            label={`Height (${unitLabel})`}
            value={props.height}
            onChange={props.onHeightChange}
            units={props.units}
            dimension={Dimension.Length}
            errorMessage={props.heightErrorMessage}
            forceValidation={props.forceValidation}
            onBlur={props.onBlur}
            minSizeWarning={props.minSizeWarning}
            widthPx={76}
            dataTestId={`${props.testIdPrefix}_height`}
          />
        </Row>
        {weightLabel !== undefined && onWeightChange !== undefined && (
          <DimensionInput
            label={weightLabel}
            value={props.weight}
            onChange={onWeightChange}
            units={props.units}
            dimension={Dimension.Weight}
            errorMessage={props.weightErrorMessage}
            forceValidation={props.forceValidation}
            onBlur={props.onBlur}
            warning={props.weightWarning}
            widthPx={130}
            dataTestId={`${props.testIdPrefix}_weight`}
          />
        )}
      </Row>
      {weightLabel !== undefined && (
        // The running total reads as one of the fields' messages, so it is
        // drawn in the same place one would be — same offset, same size — and
        // takes no height of its own, so it can move neither this row nor the
        // form below it. It gives way to a real message: with a field in error
        // the two sets of words would land on top of each other.
        <div className="relative h-0">
          {/* `text-xs` on the box, not just the words: the line box is what
              positions them, and at the inherited 16px it is eight pixels
              taller than the text, which sank this line below the field
              messages it is supposed to sit level with. */}
          <div className="absolute inset-x-0 -top-4 text-xs">
            <T color="secondary" size="xs">
              {messageInTheWay ? " " : (props.summary ?? " ")}
            </T>
          </div>
        </div>
      )}
    </div>
  );
}
